// Event-specific corrections, not guesses based on a school's normal arena.
// Dates and team identities must agree even when the NCAA id is known.
import { canonicalTeamSlug } from './teamIdentity.js';

const VERIFIED_AT = '2026-09-07T16:00:00Z';
export const VERIFIED_EVENT_METADATA = [
  { id: '6626229', date: '2026-09-06', teams: ['missouri', 'nebraska'],
    venue: 'Wrigley Field, Chicago, Ill.', neutralSite: true, network: 'FOX',
    status: 'suspended', pointsAtSuspension: 18, statusValidUntil: '2026-09-10T16:00:00Z',
    sourceUrl: 'https://huskers.com/news/2026/09/7/nebraska-missouri-volleyball-match-suspended' },
  { id: '6626809', date: '2026-09-06', teams: ['kentucky', 'penn-st'],
    venue: 'Wrigley Field, Chicago, Ill.', neutralSite: true, network: 'FOX',
    sourceUrl: 'https://www.ncaa.com/live-updates/volleyball-women/d1/volleyball-wrigley-field-live-updates-scores' },
  { id: '6624446', date: '2026-09-07', teams: ['george-mason', 'maryland'],
    venue: 'Stephen M. Schanwald Pavilion at XFINITY Center', network: 'B1G+',
    sourceUrl: 'https://umterps.com/news/2026/9/3/volleyball-terps-back-at-home' },
  { id: '6624240', date: '2026-09-07', teams: ['indiana', 'southern-ind'],
    venue: 'Liberty Arena, Evansville, Ind.', network: 'ESPN+',
    sourceUrl: 'https://iuhoosiers.com/news/2026/9/4/womens-volleyball-volleyball-central-tennessee-and-southern-indiana' },
  { id: '6628139', date: '2026-09-07', teams: ['penn-st', 'depaul'],
    venue: 'McGrath-Phillips Arena', network: 'ESPNU',
    sourceUrl: 'https://depaulbluedemons.com/news/2026/9/6/womens-volleyball-blue-demons-welcome-no-13-penn-state-to-mcgrath-phillips-arena' },
  { id: '6627728', date: '2026-09-07', teams: ['grambling', 'alabama'],
    venue: 'Foster Auditorium', network: 'SEC Network+',
    sourceUrl: 'https://rolltide.com/sports/womens-volleyball/schedule/text' },
  { id: '6627131', date: '2026-09-07', teams: ['new-mexico-st', 'new-mexico'],
    venue: 'The Pit — Powered by Nusenda', network: 'Mountain West Network',
    startTimeIso: '2026-09-08T00:30:00.000Z', timeZone: 'America/Denver',
    sourceUrl: 'https://golobos.com/sports/wvball/schedule' },
].map((entry) => ({ ...entry, verifiedAt: VERIFIED_AT }));

export function applyVerifiedEventMetadata(game, now = Date.now()) {
  const id = String(game.gameId || game.id || '');
  const date = String(game.date || game.scheduleDate || game.dateKey || '').slice(0, 10);
  const teams = [game.away, game.home].map((team) => canonicalTeamSlug(team?.slug || team?.name)).sort().join('|');
  const entry = VERIFIED_EVENT_METADATA.find((item) => item.teams.slice().sort().join('|') === teams &&
    (date ? date === item.date : id === item.id) && (!/^\d+$/.test(id) || id === item.id));
  if (!entry) return game;
  const newer = Date.parse(game.metadataVerifiedAt || '') > Date.parse(entry.verifiedAt);
  const output = { ...game };
  for (const field of ['venue', 'network', 'neutralSite', 'timeZone']) {
    if (entry[field] !== undefined && (!newer || !game[field])) output[field] = entry[field];
  }
  if (entry.startTimeIso && !newer) {
    Object.assign(output, { startTimeIso: entry.startTimeIso, startTime: entry.startTimeIso,
      startTimeEpoch: Date.parse(entry.startTimeIso) / 1000, timeConfidence: 'verified-event-schedule',
      timeLabel: new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(new Date(entry.startTimeIso)) });
  }
  if (!newer) Object.assign(output, { metadataVerifiedAt: entry.verifiedAt, metadataSourceUrl: entry.sourceUrl });
  if (entry.status) {
    const points = [game.away, game.home].flatMap((team) => team?.setScores || []).reduce((total, value) => total + (Number(value) || 0), 0);
    const finalized = /\b(final|completed|canceled|cancelled)\b/i.test(String(game.status) + ' ' + String(game.statusLabel)) ||
      Number(game.away?.score) === 3 || Number(game.home?.score) === 3;
    const newerStatus = Date.parse(game.statusVerifiedAt || '') > Date.parse(entry.verifiedAt);
    // New scoring or a final is affirmative evidence of resumption. An unchanged
    // old live flag isn't. Expired overrides become unconfirmed, not fake Live.
    if (!finalized && !newerStatus && points <= entry.pointsAtSuspension) {
      const expired = now > Date.parse(entry.statusValidUntil);
      Object.assign(output, { status: expired ? 'unconfirmed' : entry.status,
        statusLabel: expired ? 'Status unconfirmed' : 'Suspended', terminalState: expired ? 'unconfirmed' : entry.status,
        rawStatus: expired ? 'Status unconfirmed' : 'Suspended', currentPeriod: '', currentSet: null,
        live: false, final: false, scheduled: false, statusVerifiedAt: entry.verifiedAt,
        statusSourceUrl: entry.sourceUrl,
        statusNote: expired ? 'The last verified status was suspended. A continuation or final result has not been verified.' : 'Match suspended. Points shown are the last available score; continuation has not been announced.' });
    } else if (game.statusVerifiedAt === entry.verifiedAt && (finalized || points > entry.pointsAtSuspension)) {
      const canceled = /cancel/i.test(String(game.status) + ' ' + String(game.statusLabel));
      const status = canceled ? 'canceled' : finalized ? 'final' : 'live';
      Object.assign(output, { status, statusLabel: status === 'live' ? 'Live' : status === 'final' ? 'Final' : 'Canceled',
        terminalState: canceled ? 'canceled' : '', rawStatus: status, statusNote: '', live: status === 'live', final: status === 'final' });
    }
  }
  return output;
}
